import React from "react";
import {
  createLabelJob,
  getLabelJob,
  getLabelDocument,
  isLabelJobId,
} from "../api/labelPrinting";
import {
  readStoredValue,
  writeStoredValue,
  removeStoredValue,
} from "../utils/storage";

function savedOperation(key) {
  try {
    const value = JSON.parse(readStoredValue(key));
    return isLabelJobId(value?.requestId) &&
      (!value.jobId || isLabelJobId(value.jobId))
      ? value
      : null;
  } catch {
    return null;
  }
}

export default function useLabelPrinting(albumId, username) {
  const key = `sleeve-label:${username}:${albumId}`;
  const [operation, setOperation] = React.useState(() => savedOperation(key));
  const operationRef = React.useRef(operation);
  const [job, setJob] = React.useState(null);
  const [pdfUrl, setPdfUrl] = React.useState("");
  const [error, setError] = React.useState(null);
  const [pending, setPending] = React.useState(false);
  const [revision, setRevision] = React.useState(0);
  const [open, setOpen] = React.useState(false);
  const [requested, setRequested] = React.useState(false);
  const busy = React.useRef(false);

  const start = () => {
    if (busy.current) return;
    try {
      const next = { requestId: crypto.randomUUID() };
      writeStoredValue(key, JSON.stringify(next));
      operationRef.current = next;
      setOperation(next);
      setRequested(true);
      setOpen(false);
      setPending(true);
      setError(null);
      busy.current = true;
    } catch {
      setOpen(true);
      setError(
        new Error(
          "Unable to create a secure request identifier. Open the application over HTTPS.",
        ),
      );
    }
  };

  React.useEffect(() => {
    if (!operation || !requested) return;
    const controller = new AbortController();
    let timer, objectUrl;
    const active = () => !controller.signal.aborted;
    setError(null);
    setJob(null);
    setPdfUrl("");
    setPending(true);
    busy.current = true;
    const fail = (failure) => {
      if (!active()) return;
      setError(failure);
      setPending(false);
      setOpen(true);
      busy.current = false;
      if ([401, 403, 404].includes(failure.status)) removeStoredValue(key);
    };
    const receive = async (current) => {
      if (!active()) return;
      if (current.albumId !== Number(albumId))
        throw new Error("Label job belongs to a different release.");
      setJob(current);
      const saved = { ...operation, jobId: current.id };
      writeStoredValue(key, JSON.stringify(saved));
      operationRef.current = saved;
      if (["queued", "rendering"].includes(current.status)) {
        timer = setTimeout(
          () =>
            getLabelJob(current.id, controller.signal)
              .then(receive)
              .catch(fail),
          2000,
        );
      } else if (current.status === "ready") {
        const blob = await getLabelDocument(current.id, controller.signal);
        if (!active()) return;
        objectUrl = URL.createObjectURL(blob);
        setPdfUrl(objectUrl);
        removeStoredValue(key);
        setPending(false);
        setOpen(true);
        busy.current = false;
      } else {
        removeStoredValue(key);
        setPending(false);
        setOpen(true);
        busy.current = false;
      }
    };
    const knownJobId = operationRef.current?.jobId;
    const initial = knownJobId
      ? getLabelJob(knownJobId, controller.signal)
      : createLabelJob(albumId, operation.requestId, controller.signal);
    initial.then(receive).catch(fail);
    return () => {
      controller.abort();
      clearTimeout(timer);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      busy.current = false;
    };
  }, [albumId, key, requested, operation, revision]);

  const retry = () => {
    if (busy.current) return;
    setOpen(false);
    setPending(true);
    setRequested(true);
    setRevision((value) => value + 1);
    busy.current = true;
  };

  const show = () => {
    if (busy.current) return;
    if (operation && !pdfUrl && job?.status !== "failed") retry();
    else start();
  };
  return {
    job,
    pdfUrl,
    error,
    pending,
    open,
    show,
    start,
    retry,
    close: () => {
      setOpen(false);
      setRequested(false);
      setPending(false);
    },
  };
}
